import { z } from 'zod'

// ─────────────────────────────────────────────────────────────────────────── //
//                        CAMERA WORKER CONTROL API                            //
//                    gozoki-backend → camera-worker host                      //
// ─────────────────────────────────────────────────────────────────────────── //

// Contract between the backend orchestrator and the control-service running on
// each bare-metal camera worker. The backend pushes the COMPLETE desired state
// of a host (every assigned store) in one idempotent PUT; the worker renders
// the files (go2rtc, openvpn, caddy site, compose) and reconciles docker.

export const CAMERA_WORKER_API_KEY_HEADER = 'X-API-Key'

// ─── Store spec (desired state of one store) ─────────────────────────────── //

const storeIdSchema = z.number().int().positive()

const hostnameSchema = z
  .string()
  .max(253)
  .regex(
    /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?(\.[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?)+$/i,
    'must be a fully-qualified DNS hostname',
  )

// Stream names become go2rtc YAML keys and URL query values; keep them to a
// safe charset so no rendered file can be broken by a name.
const streamNameSchema = z.string().regex(/^[\w.-]+$/, 'must only contain [A-Za-z0-9_.-]')

const portSchema = z.number().int().min(1).max(65_535)

const imageSchema = z.string().min(1).regex(/^\S+$/, 'must be a docker image reference')

export const cameraWorkerStreamSchema = z.object({
  name: streamNameSchema,
  url: z
    .string()
    .min(1)
    .regex(/^[^\s]+$/, 'must not contain whitespace'),
})

export type CameraWorkerStream = z.infer<typeof cameraWorkerStreamSchema>

// Parameters of the Caddy forward_auth block: every go2rtc request is checked
// against the backend first, authenticated by a shared secret header.
export const cameraWorkerViewerAuthSchema = z.object({
  upstreamUrl: z.url(),
  path: z.string().startsWith('/'),
  secretHeader: z.string().regex(/^[A-Za-z0-9-]+$/),
  storeIdHeader: z.string().regex(/^[A-Za-z0-9-]+$/),
  secret: z.string().min(1),
})

export type CameraWorkerViewerAuth = z.infer<typeof cameraWorkerViewerAuthSchema>

const baseStoreSpecShape = {
  storeId: storeIdSchema,
  hostname: hostnameSchema,
  // Stable 0-based slot on the host; the published ports derive from it so
  // adding/removing a store never reshuffles the others.
  slot: z.number().int().min(0),
  ports: z.object({ webrtc: portSchema }),
  // COMPLETE list of go2rtc streams for the store (desired state, not a delta).
  streams: z.array(cameraWorkerStreamSchema).min(1),
  // Names of streams go2rtc should keep warm (slow-to-start sources, e.g. JVC).
  preloadStreams: z.array(streamNameSchema).default([]),
  viewerAuth: cameraWorkerViewerAuthSchema,
}

export const cameraWorkerJvcStoreSpecSchema = z.object({
  kind: z.literal('jvc'),
  ...baseStoreSpecShape,
  images: z.object({ go2rtc: imageSchema }),
})

export const cameraWorkerVpnStoreSpecSchema = z.object({
  kind: z.literal('unifi-vpn'),
  ...baseStoreSpecShape,
  images: z.object({ go2rtc: imageSchema, openvpn: imageSchema }),
  vpn: z.object({
    // Full OpenVPN client config, inline certificates included, already
    // normalized by the backend (auth-user-pass rewritten to /vpn/vpn.auth).
    config: z.string().min(1),
    credentials: z.string().regex(/^[^:\r\n]+:[^\r\n]+$/, 'must be user:password'),
  }),
})

export const cameraWorkerStoreSpecSchema = z.discriminatedUnion('kind', [
  cameraWorkerJvcStoreSpecSchema,
  cameraWorkerVpnStoreSpecSchema,
])

export type CameraWorkerStoreSpec = z.infer<typeof cameraWorkerStoreSpecSchema>
export type CameraWorkerStoreSpecInput = z.input<typeof cameraWorkerStoreSpecSchema>

// ─── PUT /state — complete desired state of the host ─────────────────────── //

export const cameraWorkerDesiredStateSchema = z
  .object({
    stores: z.array(cameraWorkerStoreSpecSchema),
  })
  .superRefine((state, ctx) => {
    const seen = {
      hostname: new Set<string>(),
      slot: new Set<number>(),
      storeId: new Set<number>(),
    }

    for (const [index, store] of state.stores.entries()) {
      if (seen.storeId.has(store.storeId)) {
        ctx.addIssue({
          code: 'custom',
          message: `duplicate storeId ${store.storeId}`,
          path: ['stores', index, 'storeId'],
        })
      }
      if (seen.slot.has(store.slot)) {
        ctx.addIssue({
          code: 'custom',
          message: `duplicate slot ${store.slot}`,
          path: ['stores', index, 'slot'],
        })
      }
      if (seen.hostname.has(store.hostname)) {
        ctx.addIssue({
          code: 'custom',
          message: `duplicate hostname ${store.hostname}`,
          path: ['stores', index, 'hostname'],
        })
      }

      seen.storeId.add(store.storeId)
      seen.slot.add(store.slot)
      seen.hostname.add(store.hostname)
    }
  })

export type CameraWorkerDesiredState = z.infer<typeof cameraWorkerDesiredStateSchema>
export type CameraWorkerDesiredStateInput = z.input<typeof cameraWorkerDesiredStateSchema>

// ─── Responses ───────────────────────────────────────────────────────────── //

export const CAMERA_WORKER_STORE_STATUSES = ['ready', 'failed'] as const

export type CameraWorkerStoreStatus = (typeof CAMERA_WORKER_STORE_STATUSES)[number]

export const cameraWorkerStoreApplyResultSchema = z.object({
  appliedHash: z.string(),
  changed: z.boolean(),
  error: z.string().nullable(),
  hostname: hostnameSchema,
  status: z.enum(CAMERA_WORKER_STORE_STATUSES),
  storeId: storeIdSchema,
})

export type CameraWorkerStoreApplyResult = z.infer<typeof cameraWorkerStoreApplyResultSchema>

export const cameraWorkerApplyStateResultSchema = z.object({
  caddyReloaded: z.boolean(),
  removedStoreIds: z.array(storeIdSchema),
  stores: z.array(cameraWorkerStoreApplyResultSchema),
  warnings: z.array(z.string()),
})

export type CameraWorkerApplyStateResult = z.infer<typeof cameraWorkerApplyStateResultSchema>

export const cameraWorkerStoreStateSchema = z.object({
  appliedHash: z.string(),
  error: z.string().nullable(),
  hostname: hostnameSchema,
  status: z.enum(CAMERA_WORKER_STORE_STATUSES),
  storeId: storeIdSchema,
  updatedAt: z.string(),
})

export type CameraWorkerStoreState = z.infer<typeof cameraWorkerStoreStateSchema>

export const cameraWorkerStateSchema = z.object({
  publicIp: z.string(),
  stores: z.array(cameraWorkerStoreStateSchema),
  uptimeSeconds: z.number(),
  workerVersion: z.string(),
})

export type CameraWorkerState = z.infer<typeof cameraWorkerStateSchema>

// Liveness of the control-service itself. The endpoint is reached THROUGH Caddy
// (which runs in Docker), so any response already proves Caddy + Docker are up —
// there is nothing useful to report about them here. Unreachable = down.
export const cameraWorkerHealthSchema = z.object({
  status: z.literal('ok'),
  uptimeSeconds: z.number(),
  workerVersion: z.string(),
})

export type CameraWorkerHealth = z.infer<typeof cameraWorkerHealthSchema>
